import { featureFlags } from '@seen/core/featureFlags'
import type { JsonObject } from '@seen/core/json'
import { InsertMedicationsButtonNode as InsertMedicationsButtonSchema } from '@seen/core/tiptap/schema'
import { Button } from '@seen/ui'
import { IconLoader2, IconPill } from '@tabler/icons-react'
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react'
import { type JSX, memo, useCallback, useState } from 'react'
import { toast } from 'sonner'
import { buildMedicationListBlock } from '#src/components/rich-text-editor/InsertMedicationsButtonNode.utils.ts'
import {
  noteButtonClass,
  noteButtonLabelClass,
} from '#src/components/rich-text-editor/noteButtonClass.ts'
import { useOptionalParticipantReference } from '#src/contexts/index.ts'
import { useCurrentMedications } from '#src/hooks/useCurrentMedications.ts'
import { useCurrentRef } from '#src/hooks/useCurrentRef.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export const InsertMedicationsButtonNode = InsertMedicationsButtonSchema.extend(
  {
    addNodeView() {
      return ReactNodeViewRenderer(InsertMedicationsButtonNodeComponent)
    },
  }
)

function InsertMedicationsButtonNodeComponent(
  props: NodeViewProps
): JSX.Element {
  return (
    <NodeViewWrapper className="inline-flex">
      <InsertMedicationsButtonNodeComponentInner {...props} />
    </NodeViewWrapper>
  )
}

const InsertMedicationsButtonNodeComponentInner = memo(
  function InsertMedicationsButtonNodeComponentInner(
    props: NodeViewProps
  ): JSX.Element {
    const participantReference = useOptionalParticipantReference()
    const {
      rxMedications,
      otcMedications,
      isLoading: isFetching,
    } = useCurrentMedications(participantReference)
    const getPosRef = useCurrentRef(props.getPos)
    const [isInserting, setIsInserting] = useState(false)

    const isLoading = isFetching || isInserting

    const handleClick = useCallback(() => {
      if (!participantReference) {
        toast.error('No participant context available')
        return
      }

      setIsInserting(true)

      try {
        const pos = getPosRef.current()
        if (pos === undefined) {
          return
        }
        props.deleteNode()

        const content: JsonObject[] = [
          ...buildMedicationListBlock('Prescription', rxMedications),
          ...buildMedicationListBlock('Over-the-Counter', otcMedications),
        ]

        if (content.length === 0) {
          props.editor
            .chain()
            .insertContentAt(pos, {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text:
                    rxMedications.length > 0 || otcMedications.length > 0
                      ? 'Unable to insert current medications'
                      : 'No current medications',
                },
              ],
            })
            .focus()
            .run()
          return
        }

        props.editor.chain().insertContentAt(pos, content).focus().run()
      } catch (_error) {
        toast.error('Failed to insert medications')
        setIsInserting(false)
      }
    }, [participantReference, getPosRef, props, rxMedications, otcMedications])

    const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
    return (
      <Button
        variant="unstyled"
        size="unstyled"
        className={noteButtonClass(inlineCapture)}
        disabled={!props.editor.isEditable || isLoading}
        onClick={handleClick}
      >
        {isLoading ? (
          <IconLoader2 className="size-3.5 shrink-0 animate-spin text-foreground-secondary" />
        ) : (
          <IconPill className="size-3.5 shrink-0 text-foreground-secondary" />
        )}
        <span className={noteButtonLabelClass(inlineCapture)}>
          Insert Current Medications
        </span>
      </Button>
    )
  }
)
